import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { View } from '@/types/views'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import usePermission from '@/hooks/usePermission'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useGetSavedViews } from '@/service-library/hooks/saved-views'
import useLocalStorage from '@/hooks/useLocalStorage'

type ViewsContextValue = ReturnType<typeof useGetSavedViews> & {
  allViews: View[]
  canEdit: boolean
  savedEnabled: boolean
  selectedView: View
  handleCancelChange: (onCancel: () => void) => void
  handleChange: () => void
  setSavedEnabled: Dispatch<SetStateAction<boolean>>
  setSelectedViewId: (newValue: string) => void
}

const ViewsContext = createContext<ViewsContextValue>({} as ViewsContextValue)

type ViewsProviderProps = {
  children: ReactNode
}

export const useViewsContext = () => useContext(ViewsContext)

export default function ViewsProvider({ children }: ViewsProviderProps) {
  const { pathname, state } = useLocation()
  const { project } = useProjectContext()
  const { hasEditingPermission } = usePermission()
  const canEditProject = hasEditingPermission('edit_projects', project.org_id)
  const [savedEnabled, setSavedEnabled] = useState(false)

  const [projDefaultSortColumn] = useLocalStorage(
    'proj-default-sort-columns',
    'enteredAtColId',
  )

  const [projDefaultSortOrder] = useLocalStorage(
    'proj-default-sort-order',
    'desc',
  )

  const { savedViews, ...rest } = useGetSavedViews({
    filters: {
      project_id: project.id,
      limit: '100',
    },
  })

  const allViews = useMemo(
    () => [
      {
        id: 'default',
        name: 'Default',
        state: {
          column_order: [],
          sorting: [
            {
              id: projDefaultSortColumn,
              desc: projDefaultSortOrder === 'desc',
            },
          ],
          org_ids: [],
          flag_codes: [],
          column_visibility: {},
          team_ids: [],
          no_flags: false,
          tag_ids: [],
        },
        project_id: project.id,
      },
      ...savedViews,
    ],
    [projDefaultSortColumn, projDefaultSortOrder, project.id, savedViews],
  )

  const {
    value: selectedViewId,
    toggleValue: setSelectedViewId,
    isInUrl,
    resetValue,
  } = useStatefulSearchParam({
    param: 'view',
    defaultValue: allViews[0].id,
  })

  const selectedView = useMemo(
    () =>
      // setting default view as fallback for when a view is deleted and selectedViewId doesn't get updated immediately or when flag is off
      allViews.find(({ id }) => id === selectedViewId) || allViews[0],
    [allViews, selectedViewId],
  )

  const handleCancelChange = (onCancel: () => void) => {
    if (savedEnabled) {
      setSavedEnabled(false)
      onCancel()
    }
  }

  const handleChange = useCallback(() => {
    if (!savedEnabled && canEditProject) {
      setSavedEnabled(true)
    }
  }, [savedEnabled, canEditProject])

  useEffect(() => {
    if (!state) {
      setSavedEnabled(false)
    }
  }, [state])

  useEffect(() => {
    if (isInUrl && ['settings', 'dashboard'].includes(pathname)) {
      resetValue()
    }
  })

  return (
    <ViewsContext.Provider
      value={{
        allViews,
        savedViews,
        canEdit: canEditProject,
        savedEnabled,
        selectedView,
        handleCancelChange,
        handleChange,
        setSavedEnabled,
        setSelectedViewId,
        ...rest,
      }}
    >
      {children}
    </ViewsContext.Provider>
  )
}
